"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Menu, X, LogIn } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex gap-1">
        <div className="h-9 w-16 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/30 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="hidden lg:flex">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative px-4 py-2 text-sm font-medium text-white transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] hover:!bg-transparent focus:!bg-transparent after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-0 after:bg-accent after:transition-all after:duration-[var(--duration-medium)] after:ease-[var(--ease-smooth)] after:-translate-x-1/2 hover:after:w-full focus:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full h-44 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isScrolled ? "bg-background/90 backdrop-blur-md shadow-medium" : "bg-background"}`}>
      <div className="container mx-auto px-4 h-full">
        <div className="flex items-center justify-between h-full">
          <Link to="/" className="flex-shrink-0">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1535234/img/image_wn9vdnfa.png" alt="VAZE REALTY" className="h-44 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)]" />
          </Link>

          <div className="flex items-center gap-6">
            <MenuProvider menu_id="39" className="hidden lg:block">
              <HeaderMenuConsumer />
            </MenuProvider>

            <div className="hidden lg:flex items-center gap-4">
              <div className="relative">
                {searchOpen ? <div className="flex items-center gap-2">
                    <Input type="search" placeholder="Search properties..." className="w-64 h-9 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)]" autoFocus />
                    <Button variant="ghost" size="icon" onClick={() => setSearchOpen(false)} className="hover:!bg-transparent focus:!bg-transparent">
                      <X className="size-5" />
                    </Button>
                  </div> : <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} className="hover:!bg-transparent focus:!bg-transparent">
                    <Search className="size-5 text-foreground" />
                  </Button>}
              </div>

              <Button asChild variant="outline" className="border-accent text-accent hover:bg-accent-dark hover:text-foreground-accent transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] shadow-soft hover:shadow-medium">
                <Link to="/client-portal">
                  <LogIn className="size-4" />
                  Client Portal
                </Link>
              </Button>

              <Button asChild className="bg-accent-dark text-foreground-accent hover:bg-accent-dark hover:scale-[1.02] active:bg-accent-dark active:scale-100 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] shadow-soft hover:shadow-medium border border-accent hover:border-accent-dark focus:!bg-accent-dark focus:!border-accent-dark">
                <Link to="/ContactPage">Contact Us</Link>
              </Button>
            </div>

            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden hover:!bg-transparent focus:!bg-transparent">
              {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>

        {mobileMenuOpen && <MenuProvider menu_id="39" className="lg:hidden">
            <MobileMenu onClose={() => setMobileMenuOpen(false)} />
          </MenuProvider>}
      </div>
    </header>;
}
function MobileMenu({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="mt-4 space-y-2 animate-fade-in-up">
        <div className="h-10 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-10 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-10 bg-muted/30 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="mt-4 pb-4 space-y-1 border-t border-muted pt-4 animate-fade-in-up">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="block px-4 py-3 text-sm font-medium text-white rounded-md hover:bg-accent/10 transition-all duration-[var(--duration-fast)] ease-[var(--ease-out)]" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="px-4 pt-2">
        <Input type="search" placeholder="Search properties..." className="w-full mb-3" />
        <Button asChild variant="outline" className="w-full mb-3 border-accent text-accent hover:bg-accent-dark hover:text-foreground-accent transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] shadow-soft hover:shadow-medium">
          <Link to="/client-portal">
            <LogIn className="size-4" />
            Client Portal
          </Link>
        </Button>
        <Button asChild className="w-full bg-accent text-foreground-accent hover:bg-accent-hover hover:scale-[1.02] active:bg-accent-active active:scale-100 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] shadow-soft hover:shadow-medium border border-accent hover:border-accent-dark">
          <Link to="/ContactPage">Contact Us</Link>
        </Button>
      </div>
    </nav>;
}